// Lightweight, dependency-free SEO hook. Sets <title>, meta description,
// Open-Graph / Twitter Cards, canonical link, and (optionally) JSON-LD
// structured-data scripts on the document head. Cleans up on unmount so
// successive page navigations don't accumulate stale tags.
//
// Usage:
//   useSEO({
//     title: "Custom T-Shirts | XYZPLUS",
//     description: "Design and print your own t-shirts...",
//     canonical: "https://xyzplus.in/custom-shop",
//     image: "https://xyzplus.in/og/custom.jpg",
//     type: "website",
//     schemas: [{ "@context": "https://schema.org", "@type": "Organization", ... }],
//   });

import { useEffect } from "react";

const SITE_ORIGIN = "https://xyzplus.in";
const SITE_NAME = "XYZPLUS";
const DEFAULT_OG_IMAGE = `${SITE_ORIGIN}/brand/og-default.jpg`;

function upsertMeta(selector, attrs) {
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    document.head.appendChild(el);
    created = true;
  }
  const prev = {};
  Object.entries(attrs).forEach(([k, v]) => {
    prev[k] = el.getAttribute(k);
    el.setAttribute(k, v);
  });
  return { el, created, prev };
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  let created = false;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
    created = true;
  }
  const prev = el.getAttribute("href");
  el.setAttribute("href", href);
  return { el, created, prev };
}

export default function useSEO(opts) {
  const {
    title,
    description,
    canonical,
    image,
    type = "website",
    schemas = [],
    noindex = false,
  } = opts || {};

  useEffect(() => {
    const prevTitle = document.title;
    const finalTitle = title ? (title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`) : prevTitle;
    if (title) document.title = finalTitle;

    const ogImage = image || DEFAULT_OG_IMAGE;
    const ogUrl = canonical || (typeof window !== "undefined" ? window.location.href : "");

    const refs = [];
    if (description) {
      refs.push(upsertMeta('meta[name="description"]', { name: "description", content: description }));
      refs.push(upsertMeta('meta[property="og:description"]', { property: "og:description", content: description }));
      refs.push(upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description }));
    }
    if (title) {
      refs.push(upsertMeta('meta[property="og:title"]', { property: "og:title", content: finalTitle }));
      refs.push(upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: finalTitle }));
    }
    refs.push(upsertMeta('meta[property="og:type"]', { property: "og:type", content: type }));
    refs.push(upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME }));
    refs.push(upsertMeta('meta[property="og:image"]', { property: "og:image", content: ogImage }));
    refs.push(upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: ogImage }));
    refs.push(upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" }));
    if (ogUrl) refs.push(upsertMeta('meta[property="og:url"]', { property: "og:url", content: ogUrl }));

    // robots
    refs.push(
      upsertMeta('meta[name="robots"]', {
        name: "robots",
        content: noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large",
      })
    );

    // Canonical
    let canonicalRef = null;
    if (canonical) canonicalRef = upsertLink("canonical", canonical);

    // JSON-LD schema scripts
    const schemaEls = (schemas || []).filter(Boolean).map((s, i) => {
      const el = document.createElement("script");
      el.type = "application/ld+json";
      el.dataset.seoSchema = `useSEO-${i}`;
      try {
        el.textContent = JSON.stringify(s);
      } catch (_e) {
        el.textContent = "{}";
      }
      document.head.appendChild(el);
      return el;
    });

    return () => {
      // Restore previous title
      document.title = prevTitle;
      // Remove created or restore prev for canonical
      if (canonicalRef) {
        if (canonicalRef.created) canonicalRef.el.remove();
        else if (canonicalRef.prev) canonicalRef.el.setAttribute("href", canonicalRef.prev);
      }
      // Schemas are page-scoped, always remove
      schemaEls.forEach((el) => el.remove());
      // We don't aggressively restore meta tags — the next page's useSEO will overwrite them.
    };
  }, [title, description, canonical, image, type, JSON.stringify(schemas), noindex]);
}

// Convenience: build a Product JSON-LD payload from a backend product object.
export function buildProductSchema(p, opts = {}) {
  if (!p) return null;
  const url = opts.url || `${SITE_ORIGIN}/p/${p.slug}`;
  const images = [];
  if (Array.isArray(p.images)) p.images.forEach((i) => i && images.push(i));
  if (p.image_url) images.push(p.image_url);
  const price = p.sale_price || p.base_price || 0;
  // Google Merchant likes a price-valid-until date. Sale prices get a tighter
  // window; full-price products are valid for ~1 year.
  const validUntil = new Date();
  validUntil.setFullYear(validUntil.getFullYear() + (p.sale_price ? 0 : 1));
  if (p.sale_price) validUntil.setDate(validUntil.getDate() + 30); // 30-day sale window
  const offers = {
    "@type": "Offer",
    url,
    priceCurrency: "INR",
    price: String(price),
    priceValidUntil: validUntil.toISOString().slice(0, 10),
    availability: (p.stock === 0 || p.is_active === false)
      ? "https://schema.org/OutOfStock"
      : "https://schema.org/InStock",
    itemCondition: "https://schema.org/NewCondition",
    seller: { "@type": "Organization", name: SITE_NAME },
    hasMerchantReturnPolicy: {
      "@type": "MerchantReturnPolicy",
      applicableCountry: "IN",
      returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
      merchantReturnDays: 7,
      returnMethod: "https://schema.org/ReturnByMail",
      returnFees: "https://schema.org/FreeReturn",
    },
    shippingDetails: {
      "@type": "OfferShippingDetails",
      shippingRate: {
        "@type": "MonetaryAmount",
        value: price >= 999 ? "0" : "60",
        currency: "INR",
      },
      shippingDestination: {
        "@type": "DefinedRegion",
        addressCountry: "IN",
      },
      deliveryTime: {
        "@type": "ShippingDeliveryTime",
        handlingTime: { "@type": "QuantitativeValue", minValue: 1, maxValue: 2, unitCode: "DAY" },
        transitTime: { "@type": "QuantitativeValue", minValue: 3, maxValue: 5, unitCode: "DAY" },
      },
    },
  };
  const out = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: p.name,
    description: p.description || p.short_description || `${p.name} from ${SITE_NAME}`,
    image: images.slice(0, 8),
    sku: p.sku || p.id || p.slug,
    mpn: p.id || p.slug,
    brand: { "@type": "Brand", name: SITE_NAME },
    offers,
  };
  if (p.rating_avg && p.rating_count) {
    out.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: String(p.rating_avg),
      reviewCount: String(p.rating_count),
      bestRating: "5",
      worstRating: "1",
    };
  }
  return out;
}

export function buildBreadcrumbSchema(items) {
  // items: [{ name, url }]
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.url,
    })),
  };
}

export function buildOrganizationSchema() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: SITE_NAME,
    url: SITE_ORIGIN,
    logo: `${SITE_ORIGIN}/brand/logo.png`,
    sameAs: [
      "https://www.instagram.com/xyzplus",
      "https://www.facebook.com/xyzplus",
    ],
  };
}

export function buildItemListSchema(products, baseUrl = SITE_ORIGIN) {
  if (!Array.isArray(products) || !products.length) return null;
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: products.slice(0, 20).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      url: `${baseUrl}/p/${p.slug}`,
      name: p.name,
    })),
  };
}

export { SITE_ORIGIN, SITE_NAME };
